import { IMediator } from '@telespot/core';
import { type CaseTypeRepository, Workspace, type WorkspaceRepository } from '@telespot/domain';
import { CREATE_WORKSPACE, CreateWorkspace, type ICreateWorkspaceHandler } from '../create-workspace';

/**
 * Input data for updating a workspace.
 * It allows for workspaceId to be omitted, in which case a new workspace should be created.
 */
export interface UpdateWorkspaceInput {
  workspaceId?: string;
  createdById: string;
  organizationId: string;
  name: string;
  caseTypeId: string;
  config: unknown;
}

/**
 * Error codes for the {@link IUpdateWorkspaceCommand} use case.
 */
export enum UpdateWorkspaceErrorCode {
  WORKSPACE_NOT_FOUND = 'Workspace with id <id> not found',
  CANNOT_CHANGE_ORGANIZATION = 'Cannot change organization of a workspace',
  CASE_TYPE_NOT_FOUND = 'Case type with id <id> not found',
  CREATION_FAILED = 'Failed to create workspace',
}

/**
 * Exception for the {@link IUpdateWorkspaceCommand} use case.
 */
export class UpdateWorkspaceException extends Error {
  constructor(public readonly code: UpdateWorkspaceErrorCode, public readonly details: Record<string, string> = {}) {
    super(code.toString().replace('<id>', details.id ?? ''));
  }
}

/**
 * Use case for updating a workspace.
 * If a workspaceId is not provided, a new workspace should be created.
 * If a workspaceId is provided, the workspace should be updated.
 * If a workspaceId is provided, but the workspace does not exist, an error should be thrown.
 */
export interface IUpdateWorkspaceCommand {
  execute(command: UpdateWorkspaceInput): Promise<string>;
}

/**
 * Use case implementation for updating a workspace.
 */
export class UpdateWorkspaceCommand implements IUpdateWorkspaceCommand {
  constructor(
    private readonly workspaces: WorkspaceRepository,
    private readonly caseTypes: CaseTypeRepository,
    private readonly mediator: IMediator<{ [CREATE_WORKSPACE]: ICreateWorkspaceHandler }>
  ) {}

  private async createWorkspace(command: UpdateWorkspaceInput): Promise<string> {
    const { createdById, organizationId, name, caseTypeId, config } = command;
    try {
      return this.mediator.send(
        new CreateWorkspace({
          name,
          organizationId,
          caseTypeId,
          createdById,
          config,
        })
      );
    } catch (error) {
      throw new UpdateWorkspaceException(UpdateWorkspaceErrorCode.CREATION_FAILED);
    }
  }

  private async validateWorkspace(workspaceId: string): Promise<Workspace> {
    try {
      const workspace = await this.workspaces.getById(workspaceId);
      if (!workspace)
        throw new UpdateWorkspaceException(UpdateWorkspaceErrorCode.WORKSPACE_NOT_FOUND, { id: workspaceId });
      return workspace;
    } catch (error) {
      throw new UpdateWorkspaceException(UpdateWorkspaceErrorCode.WORKSPACE_NOT_FOUND, { id: workspaceId });
    }
  }

  private async updateWorkspace(command: UpdateWorkspaceInput): Promise<string> {
    // make sure the workspace exists
    const workspace = await this.validateWorkspace(command.workspaceId);

    // make sure the organization is not being updated
    if (workspace.organizationId !== command.organizationId)
      throw new UpdateWorkspaceException(UpdateWorkspaceErrorCode.CANNOT_CHANGE_ORGANIZATION, {
        id: command.workspaceId,
      });

    // make sure the case type is valid
    const caseType = await this.caseTypes.getById(command.caseTypeId);
    if (!caseType)
      throw new UpdateWorkspaceException(UpdateWorkspaceErrorCode.CASE_TYPE_NOT_FOUND, { id: command.caseTypeId });

    // create a new instance of the workspace with the modified attributes
    const updatedWorkspace = new Workspace({
      id: workspace.id,
      name: command.name,
      organizationId: command.organizationId,
      caseTypeId: command.caseTypeId,
      createdById: workspace.createdById,
      config: command.config,
      numCases: workspace.numCases,
      numMembers: workspace.numMembers,
      usedStorage: workspace.usedStorage,
      updatedAt: new Date(),
    });

    return this.workspaces.save(updatedWorkspace);
  }

  public async execute(command: UpdateWorkspaceInput): Promise<string> {
    if (!command.workspaceId) {
      return this.createWorkspace(command);
    } else {
      return this.updateWorkspace(command);
    }
  }
}
